import React, { useState } from 'react'
import { Plus, Trash2, Users, ChevronRight, ChevronLeft, Loader2 } from 'lucide-react'
import { AdImpactClient } from '../../lib/alertClientsApi'

const OFFICES = ['US Senate', 'US House', 'Downballot', 'Governor', 'Issue', 'Presidential', 'National']
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
  'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
]

const YELLOW = '#eab308'
const YELLOW_HOVER = '#facc15'

const emptyIndividual = () => ({
  client_name: '',
  email: '',
  delta_alerts: false,
  admo_alerts: false,
  admo_plus_alerts: false,
  ctv_alerts: false,
  dashboards: false,
  digital_dashboards: false,
})

const fieldStyle = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 13,
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
}

export default function AdImpactAddClientsModal({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [banner, setBanner] = useState(null)

  const [raceInfo, setRaceInfo] = useState({
    election: '',
    office: '',
    state: '',
    date_added: new Date().toISOString().split('T')[0],
    account_executive: '',
  })

  const [individuals, setIndividuals] = useState([emptyIndividual()])

  const resetForm = () => {
    setStep(1)
    setBanner(null)
    setRaceInfo({
      election: '',
      office: '',
      state: '',
      date_added: new Date().toISOString().split('T')[0],
      account_executive: '',
    })
    setIndividuals([emptyIndividual()])
  }

  const close = () => {
    resetForm()
    onOpenChange(false)
  }

  const addRow = () => setIndividuals(prev => [...prev, emptyIndividual()])
  const removeRow = idx => setIndividuals(prev => prev.filter((_, i) => i !== idx))

  const updateIndividual = (idx, field, value) => {
    setIndividuals(prev => prev.map((ind, i) => (i === idx ? { ...ind, [field]: value } : ind)))
  }

  const canProceedStep1 = raceInfo.election && raceInfo.office
  const canSubmit = individuals.some(ind => ind.client_name && ind.email)

  const handleSubmit = async () => {
    const validRows = individuals.filter(ind => ind.client_name && ind.email)
    if (validRows.length === 0) {
      setBanner({ type: 'error', text: 'Add at least one client with name and email.' })
      return
    }

    setSaving(true)
    setBanner(null)
    try {
      const records = validRows.map(ind => ({ ...raceInfo, ...ind }))
      await AdImpactClient.bulkCreate(records)
      resetForm()
      onOpenChange(false)
      onSuccess()
    } catch (err) {
      console.error(err)
      setBanner({ type: 'error', text: 'Failed to save clients. Please try again.' })
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  const primaryBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    fontSize: 14,
    fontWeight: 600,
    border: 'none',
    borderRadius: 8,
    backgroundColor: YELLOW,
    color: '#000',
    cursor: saving ? 'wait' : 'pointer',
    fontFamily: 'inherit',
    opacity: saving ? 0.85 : 1,
  }

  const outlineBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    fontSize: 14,
    fontWeight: 500,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    backgroundColor: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontFamily: 'inherit',
  }

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(15, 23, 42, 0.5)',
        padding: 16,
      }}
      onClick={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="adimpact-add-title"
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 896,
          maxHeight: '90vh',
          overflowY: 'auto',
          backgroundColor: '#fff',
          borderRadius: 12,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          padding: 24,
        }}
      >
        <h2 id="adimpact-add-title" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px', fontSize: 18, fontWeight: 600 }}>
          <Users size={20} color={YELLOW} />
            AdImpact Client Setup — Step {step} of 2
        </h2>

        {banner && (
          <div
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 8,
              fontSize: 13,
              backgroundColor: banner.type === 'error' ? '#fef2f2' : '#f0fdf4',
              color: banner.type === 'error' ? '#b91c1c' : '#166534',
            }}
          >
            {banner.text}
          </div>
        )}

        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>Enter shared race information for this batch of clients.</p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 16,
              }}
            >
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Election / Race *</span>
                <input
                  placeholder="e.g. PA-01, TX Senate"
                  value={raceInfo.election}
                  onChange={e => setRaceInfo(p => ({ ...p, election: e.target.value }))}
                  style={fieldStyle}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Office *</span>
                <select
                  value={raceInfo.office}
                  onChange={e => setRaceInfo(p => ({ ...p, office: e.target.value }))}
                  style={fieldStyle}
                >
                  <option value="">Select office</option>
                  {OFFICES.map(o => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>State</span>
                <select
                  value={raceInfo.state || ''}
                  onChange={e => setRaceInfo(p => ({ ...p, state: e.target.value }))}
                  style={fieldStyle}
                >
                  <option value="">— None —</option>
                  <option value="National">National</option>
                  {US_STATES.map(s => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Date Added</span>
                <input
                  type="date"
                  value={raceInfo.date_added}
                  onChange={e => setRaceInfo(p => ({ ...p, date_added: e.target.value }))}
                  style={fieldStyle}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: '1 / -1' }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Account Executive</span>
                <input
                  placeholder="Name"
                  value={raceInfo.account_executive}
                  onChange={e => setRaceInfo(p => ({ ...p, account_executive: e.target.value }))}
                  style={fieldStyle}
                />
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                disabled={!canProceedStep1}
                onClick={() => setStep(2)}
                style={{
                  ...primaryBtn,
                  opacity: canProceedStep1 ? 1 : 0.5,
                  cursor: canProceedStep1 ? 'pointer' : 'not-allowed',
                }}
                onMouseEnter={e => canProceedStep1 && (e.currentTarget.style.backgroundColor = YELLOW_HOVER)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = YELLOW)}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                Add individuals for <strong>{raceInfo.election}</strong> ({raceInfo.office})
              </p>
              <button type="button" style={{ ...outlineBtn, height: 32, fontSize: 12 }} onClick={addRow}>
                <Plus size={14} /> Add Row
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {individuals.map((ind, idx) => (
                <div
                  key={idx}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 12,
                    backgroundColor: '#f9fafb',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', width: 24 }}>#{idx + 1}</span>
                    <input
                      placeholder="Client Name"
                      value={ind.client_name}
                      onChange={e => updateIndividual(idx, 'client_name', e.target.value)}
                      style={{ ...fieldStyle, flex: '1 1 140px', minWidth: 120, height: 32, padding: '4px 8px', fontSize: 13 }}
                    />
                    <input
                      placeholder="Email"
                      value={ind.email}
                      onChange={e => updateIndividual(idx, 'email', e.target.value)}
                      style={{ ...fieldStyle, flex: '1 1 180px', minWidth: 160, height: 32, padding: '4px 8px', fontSize: 13 }}
                    />
                    {individuals.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#9ca3af',
                          cursor: 'pointer',
                          padding: 4,
                        }}
                        title="Remove row"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 20px', marginTop: 10, paddingLeft: 32 }}>
                    {[
                      { key: 'delta_alerts', label: 'Delta' },
                      { key: 'admo_alerts', label: 'AdMo' },
                      { key: 'admo_plus_alerts', label: 'AdMo+' },
                      { key: 'ctv_alerts', label: 'CTV' },
                      { key: 'dashboards', label: 'Dash' },
                      { key: 'digital_dashboards', label: 'Digital' },
                    ].map(({ key, label }) => (
                      <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#4b5563', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!ind[key]}
                          onChange={e => updateIndividual(idx, key, e.target.checked)}
                          style={{ width: 16, height: 16, accentColor: YELLOW }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <button type="button" style={outlineBtn} onClick={() => setStep(1)}>
                <ChevronLeft size={16} /> Back
              </button>
              <button
                type="button"
                disabled={!canSubmit || saving}
                onClick={handleSubmit}
                style={{
                  ...primaryBtn,
                  opacity: !canSubmit || saving ? 0.6 : 1,
                  cursor: !canSubmit || saving ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={e => canSubmit && !saving && (e.currentTarget.style.backgroundColor = YELLOW_HOVER)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = YELLOW)}
              >
                {saving ? <Loader2 size={16} style={{ animation: 'adimpact-spin 0.8s linear infinite' }} /> : null}
                Save {individuals.filter(i => i.client_name && i.email).length} Client(s)
              </button>
            </div>
          </div>
        )}
      </div>
      <style>{`
        @keyframes adimpact-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  )
}
